import {Injectable} from '@angular/core';

export interface RouteMenuItem {
  title: string;
  route?: string;
  subMenu?: RouteMenuItem[];
  subsubMenu?: RouteMenuItem[];
}

@Injectable({
  providedIn: 'root',
})
export class NavigationRouteInheritanceService {
  getActiveTitlePath(route: string, menuItems: RouteMenuItem[]): string[] {
    return this.getActiveNodePath(route, menuItems).map(item => item.title);
  }

  getActiveNodePath(
    route: string,
    menuItems: RouteMenuItem[],
  ): RouteMenuItem[] {
    if (!menuItems?.length) {
      return [];
    }

    const normalizedCurrentRoute = this.normalizePath(route);
    let bestMatch: {path: RouteMenuItem[]; routeLength: number} | null = null;

    const visit = (
      items: RouteMenuItem[],
      parentPath: RouteMenuItem[],
    ): void => {
      items.forEach(item => {
        const currentPath = [...parentPath, item];

        if (item.route) {
          const normalizedItemRoute = this.normalizePath(item.route);

          if (this.isRouteMatch(normalizedCurrentRoute, normalizedItemRoute)) {
            const currentMatch = {
              path: currentPath,
              routeLength: normalizedItemRoute.length,
            };

            if (this.isBetterMatch(currentMatch, bestMatch)) {
              bestMatch = currentMatch;
            }
          }
        }

        const children = this.getChildren(item);
        if (children.length > 0) {
          visit(children, currentPath);
        }
      });
    };

    visit(menuItems, []);

    return this.getMatchPath(bestMatch);
  }

  private getMatchPath(
    match: {path: RouteMenuItem[]; routeLength: number} | null,
  ): RouteMenuItem[] {
    if (!match) {
      return [];
    }

    return match.path;
  }

  private getChildren(item: RouteMenuItem): RouteMenuItem[] {
    const children: RouteMenuItem[] = [];

    if (Array.isArray(item.subMenu)) {
      children.push(...item.subMenu);
    }

    if (Array.isArray(item.subsubMenu)) {
      children.push(...item.subsubMenu);
    }

    return children;
  }

  private isRouteMatch(currentRoute: string, itemRoute: string): boolean {
    if (currentRoute === itemRoute) {
      return true;
    }

    return currentRoute.startsWith(`${itemRoute}/`);
  }

  private isBetterMatch(
    current: {path: RouteMenuItem[]; routeLength: number},
    best: {path: RouteMenuItem[]; routeLength: number} | null,
  ): boolean {
    if (!best) {
      return true;
    }

    if (current.routeLength > best.routeLength) {
      return true;
    }

    if (current.routeLength < best.routeLength) {
      return false;
    }

    return current.path.length > best.path.length;
  }

  private normalizePath(path: string): string {
    if (!path) {
      return '/';
    }

    let normalized = path.trim();

    if (/^https?:\/\//.test(normalized)) {
      try {
        normalized = new URL(normalized).pathname;
      } catch {
        normalized = '/';
      }
    }

    normalized = normalized.split('?')[0]?.split('#')[0] ?? '/';

    normalized = normalized
      .split('/')
      .map(segment => segment.split(';')[0] ?? '')
      .join('/');

    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }

    normalized = normalized.replace(/\/{2,}/g, '/');

    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized || '/';
  }
}
